import React, { useState, useEffect } from "react";
import { IPanelScope, ITreeItem } from "@/interfaces";
import { Loading } from "@/shared-components";
import { FOLDERS_TAB_KEY, MAX_TREE_LEVELS } from "@/constants";
import { ListItemRow } from "./components/ListItemRow";
import { useFavoritesContext } from "@/contexts";
import styles from "./PtTreeItem.module.scss";
import { FavoriteItem, PanelSettings } from "@/types";

interface PtTreeItemProp {
  scope: IPanelScope;
  level: number;
  selectedItemId: string | undefined;
  data: ITreeItem;
  criteria: string;
  enableSettings: boolean;
  hasEditPermissions: boolean;
  openNodes: string[];
  settings: PanelSettings;
  contextMenu: (props: {
    data: ITreeItem;
    isSelected: boolean;
    setIsEditable: React.Dispatch<React.SetStateAction<boolean>>;
  }) => JSX.Element;
  notifyFilterChanged: (node: ITreeItem) => void;
  handleTreeStateChange: (node: ITreeItem, fetchNeeded: boolean) => void;
  handleNodeRename?: (id: string, value: string) => Promise<void>;
}

export const PtTreeItem: React.FC<PtTreeItemProp> = ({
  scope,
  data,
  openNodes,
  settings,
  level,
  selectedItemId,
  contextMenu,
  enableSettings,
  hasEditPermissions,
  criteria,
  handleNodeRename,
  notifyFilterChanged,
  handleTreeStateChange,
}) => {
  const { id, children, extra } = data;

  const [isTreeNodeExpand, setIsTreeNodeExpand] = useState<boolean>(false);
  const [userExpanded, setUserExpanded] = useState<boolean>(false);

  const parentLevels = data.serverObject?.ancestry
    ? data.serverObject.ancestry.split("/").length
    : 0;

  const effectiveMaxLevels = MAX_TREE_LEVELS + parentLevels;
  const hasChildren =
    level < effectiveMaxLevels &&
    (data.children.length > 0 || extra.hasChildren);

  const isLoading = extra.isLoading;
  const isSelected = id == selectedItemId;
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const {
    maxNumberOfFavorites,
    favorites,
    toggleFavorite,
    activeFavorite,
    handleActiveFavoriteChanged,
    topLevelCount,
  } = useFavoritesContext();

  useEffect(() => {
    setIsFavorite(favorites.some((f) => f.id == data.id));
  }, [favorites, data.id]);

  useEffect(() => {
    setIsTreeNodeExpand(
      isLoading ||
        (openNodes.includes(id) && (!!data.children.length || userExpanded)),
    );
  }, [openNodes, id, isLoading, userExpanded]);

  const onExpandStateChanged = async (): Promise<void> => {
    const willExpand = !isTreeNodeExpand;
    setUserExpanded(willExpand);
    setIsTreeNodeExpand(willExpand);
    handleTreeStateChange(data, willExpand);
  };

  const paddingLeft = 16 * level;
  let newLevel = level;
  if (hasChildren) {
    newLevel++;
  }

  const handleNodeClicked = (e: React.MouseEvent, node: ITreeItem) => {
    e.stopPropagation();
    notifyFilterChanged(node);
    if (node.id !== activeFavorite) {
      handleActiveFavoriteChanged(null);
    }
  };

  const getContextMenu = (isShow: boolean) => {
    if (
      !enableSettings ||
      extra.isContextMenuDisabled ||
      !isShow ||
      isEditable
    ) {
      return (
        <span
          className={
            isEditable ? styles["editable-placeholder"] : styles.placeholder
          }
        ></span>
      );
    }
    return contextMenu({ data, isSelected, setIsEditable });
  };

  const handleRename = async (value: string): Promise<void> => {
    if (!handleNodeRename) return;

    if (data.name === value) {
      setIsEditable(false);
      return;
    }

    await handleNodeRename(data.id, value);
    setIsEditable(false);
  };

  const onFavoriteClicked = async () => {
    if (isFavorite || topLevelCount(favorites) < maxNumberOfFavorites) {
      await toggleFavorite(data);
      setIsFavorite(!isFavorite);
    }
  };

  return (
    <div
      {...(isSelected ? { id: "activeFilter" } : {})}
      {...(scope.tab === FOLDERS_TAB_KEY && { "data-folder-id": data.id })}
    >
      <ListItemRow
        data={data}
        paddingLeft={paddingLeft}
        isSelected={isSelected}
        hasChildren={hasChildren}
        isEditable={isEditable}
        scope={scope}
        criteria={criteria}
        isFavorite={isFavorite}
        isTreeNodeExpand={isTreeNodeExpand}
        onExpandStateChanged={onExpandStateChanged}
        handleRename={handleRename}
        handleNodeClicked={
          handleNodeClicked as (
            e: React.MouseEvent,
            node: ITreeItem | FavoriteItem,
          ) => void
        }
        handleFavoriteClick={onFavoriteClicked}
        getContextMenu={getContextMenu}
      />
      {extra.isLoading ? (
        <li className={styles.loaderContainer}>
          <Loading size="modal" />
        </li>
      ) : (
        (hasChildren || isLoading) && (
          <ul
            id={`tree_child_${id}`}
            className={isTreeNodeExpand ? styles.expand : styles.collapse}
          >
            {isTreeNodeExpand &&
              children.map((item: ITreeItem) => (
                <PtTreeItem
                  settings={settings}
                  key={item.id} // Use `item.id` for the key instead of `item.key`
                  openNodes={openNodes}
                  criteria={criteria}
                  enableSettings={enableSettings}
                  scope={scope}
                  data={item}
                  level={newLevel}
                  selectedItemId={selectedItemId}
                  hasEditPermissions={hasEditPermissions}
                  contextMenu={contextMenu}
                  notifyFilterChanged={notifyFilterChanged}
                  handleTreeStateChange={handleTreeStateChange}
                  handleNodeRename={handleNodeRename}
                />
              ))}
          </ul>
        )
      )}
    </div>
  );
};
